/**
 * Smart-scroll behaviour for the "Start Your Pulse Brief" button.
 *
 * When the current page already contains the on-page lead form (id="pulse-brief"),
 * clicking the button should smooth-scroll the user directly to it — no route
 * change, no jarring page reload, no scroll-hunt for the form.
 *
 * When the form is NOT on the current page, the button falls back to the
 * standard <Link to="/contact"> behaviour, so router navigation still fires.
 *
 * Usage in a component:
 *   import { handlePulseBriefClick } from "@/lib/pulseBriefCta";
 *   ...
 *   <Link to="/contact" onClick={handlePulseBriefClick}>Start Your Pulse Brief</Link>
 */

export const PULSE_BRIEF_ANCHOR_ID = "pulse-brief";

// Lazy-imported to avoid pulling analytics module into non-analytics call sites
// if this helper is bundled in isolation. In practice the module is small.
import { trackStartBrief } from "@/lib/analytics";

export function handlePulseBriefClick(e) {
    if (typeof document === "undefined") return;

    // Fire the start_brief event on every CTA click. Placement is inferred
    // from the calling context (nav header, mobile menu, footer, sticky bar,
    // section CTAs). We don't have direct source context here — pass the
    // current pathname as source_page hint via a safe read of window.location.
    let sourcePage = "unknown";
    try {
        sourcePage = window.location && window.location.pathname ? window.location.pathname : "unknown";
    } catch (_e) {
        // ignore
    }
    trackStartBrief({
        placement: "cta_link",
        source: sourcePage,
        target: "pulse_brief_form",
    });

    const target = document.getElementById(PULSE_BRIEF_ANCHOR_ID);
    if (!target) return; // Not on this page — let the router navigate to /contact.

    // Prevent the router link from firing so we do not add a history entry
    // or reload the page. Then perform a smooth scroll to the form.
    if (e && typeof e.preventDefault === "function") {
        e.preventDefault();
    }

    // Respect reduced-motion preference.
    const prefersReduced =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    target.scrollIntoView({
        behavior: prefersReduced ? "auto" : "smooth",
        block: "start",
    });

    // Move focus to the first form field for keyboard users. Defer until the
    // scroll starts so the browser has time to reveal the section.
    window.setTimeout(() => {
        const firstField = target.querySelector(
            'input:not([type="hidden"]), textarea, [role="combobox"], select'
        );
        if (firstField && typeof firstField.focus === "function") {
            try {
                firstField.focus({ preventScroll: true });
            } catch (_e) {
                // ignore
            }
        }
    }, 450);
}
